import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'accent' | 'light' | 'outline-light'

export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonVariantOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary/90',
    outline: 'border border-foreground/15 bg-card text-foreground hover:border-foreground/30 hover:bg-muted',
    ghost: 'text-foreground hover:bg-muted',
    accent: 'bg-accent text-accent-foreground shadow-md shadow-accent/20 hover:bg-accent/85',
    light: 'bg-background text-foreground hover:bg-background/90',
    'outline-light': 'border border-background/35 text-background hover:bg-background/10'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-base'
}

export const buttonVariants = ({ variant = 'primary', size = 'md', className }: ButtonVariantOptions = {}): string => {
    return cn(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-60 [&_svg]:size-4 [&_svg]:shrink-0',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={buttonVariants({ variant, size, className })} {...props}>
            {children}
        </Link>
    )
}
